import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "row mb-10" }
const _hoisted_3 = { class: "col span-6" }
const _hoisted_4 = { class: "row mb-10" }
const _hoisted_5 = { class: "col span-6" }
const _hoisted_6 = { class: "col span-6" }
const _hoisted_7 = {
  role: "status",
  "data-testid": "disk-storage-options-native-status"
}

import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from '@shell/composables/useI18n';
import LabeledSelect from '@shell/components/form/LabeledSelect';
import { Checkbox } from '@components/Form/Checkbox';
import { Banner } from '@components/Banner';
import { _CREATE } from '@shell/config/query-params';
import {
  DISK_CACHE_MODES, DISK_IO_MODES, PROFILE, Disk, applyPerformanceOptions, detectProfile, hasPerformanceOptions, requestCustomProfile
} from './storage-options';


export default /*@__PURE__*/_defineComponent({
  __name: 'DiskStorageOptions',
  props: {
    value: {},
    mode: { default: _CREATE },
    disabled: { type: Boolean, default: false }
  },
  emits: ["update:value"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const store = useStore();
const { t } = useI18n(store);

const profile = computed(() => detectProfile(props.value));

// The driver rejects storage options on a CD-ROM
const isCdRom = computed(() => props.value.type === 'cd-rom');

const isCustom = computed(() => profile.value === PROFILE.CUSTOM);

const isHighPerformance = computed(() => profile.value === PROFILE.HIGH);

// The driver defaults an unset bus to virtio. KubeVirt rejects a dedicated I/O thread on any other bus.
const isVirtio = computed(() => !props.value.bus || props.value.bus === 'virtio');

// Only worth pointing out once the disk uses an option, as Harvester does
const showBusWarning = computed(() => !isVirtio.value && hasPerformanceOptions(props.value));

const profileOptions = computed(() => Object.values(PROFILE).map((value) => ({
  label:    t(`cluster.credential.harvester.storageOptions.profile.${ value }`),
  value,
  disabled: value === PROFILE.HIGH && !isVirtio.value,
})));

const cacheOptions = computed(() => DISK_CACHE_MODES.map((value) => ({
  label:    t(`cluster.credential.harvester.storageOptions.cache.${ value || 'default' }`),
  value,
  disabled: props.value.io === 'native' && value !== 'none',
})));

const ioOptions = computed(() => DISK_IO_MODES.map((value) => ({
  label: t(`cluster.credential.harvester.storageOptions.io.${ value || 'default' }`),
  value,
})));

function set(options: Disk) {
  emit('update:value', applyPerformanceOptions(props.value, options));
}

function setProfile(profile: string) {
  requestCustomProfile(props.value, profile === PROFILE.CUSTOM);

  if (profile === PROFILE.DEFAULT) {
    set({
      cache: '', io: '', dedicatedIOThread: false
    });
  } else if (profile === PROFILE.HIGH) {
    set({
      cache: 'none', io: 'native', dedicatedIOThread: true
    });
  }
}

return (_ctx: any,_cache: any) => {
  return (!isCdRom.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_unref(LabeledSelect), {
              value: profile.value,
              "data-testid": "disk-storage-options-profile",
              label: _unref(t)('cluster.credential.harvester.storageOptions.profile.label'),
              tooltip: _unref(t)('cluster.credential.harvester.storageOptions.profile.tip'),
              options: profileOptions.value,
              mode: __props.mode,
              disabled: __props.disabled,
              "onUpdate:value": setProfile
            }, null, 8, ["value", "label", "tooltip", "options", "mode", "disabled"])
          ])
        ]),
        _cache[6] || (_cache[6] = _createTextVNode()),
        (isHighPerformance.value)
          ? (_openBlock(), _createBlock(_unref(Banner), {
              key: 0,
              color: "info",
              label: _unref(t)('cluster.credential.harvester.storageOptions.highPerformanceTip')
            }, null, 8, ["label"]))
          : _createCommentVNode("", true),
        _cache[7] || (_cache[7] = _createTextVNode()),
        (showBusWarning.value)
          ? (_openBlock(), _createBlock(_unref(Banner), {
              key: 1,
              color: "warning",
              "data-testid": "disk-storage-options-bus-warning",
              label: _unref(t)('cluster.credential.harvester.storageOptions.busWarning', { bus: __props.value.bus })
            }, null, 8, ["label"]))
          : _createCommentVNode("", true),
        _cache[8] || (_cache[8] = _createTextVNode()),
        (isCustom.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(_unref(LabeledSelect), {
                    value: __props.value.cache || '',
                    "data-testid": "disk-storage-options-cache",
                    label: _unref(t)('cluster.credential.harvester.storageOptions.cache.label'),
                    tooltip: _unref(t)('cluster.credential.harvester.storageOptions.cache.tip'),
                    options: cacheOptions.value,
                    mode: __props.mode,
                    disabled: __props.disabled,
                    "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => (set({ cache: $event })))
                  }, null, 8, ["value", "label", "tooltip", "options", "mode", "disabled"])
                ]),
                _cache[3] || (_cache[3] = _createTextVNode()),
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(_unref(LabeledSelect), {
                    value: __props.value.io || '',
                    "data-testid": "disk-storage-options-io",
                    label: _unref(t)('cluster.credential.harvester.storageOptions.io.label'),
                    tooltip: _unref(t)('cluster.credential.harvester.storageOptions.io.tip'),
                    options: ioOptions.value,
                    mode: __props.mode,
                    disabled: __props.disabled,
                    "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => (set({ io: $event })))
                  }, null, 8, ["value", "label", "tooltip", "options", "mode", "disabled"])
                ])
              ]),
              _cache[4] || (_cache[4] = _createTextVNode()),
              _createVNode(_unref(Checkbox), {
                value: !!__props.value.dedicatedIOThread,
                "data-testid": "disk-storage-options-dedicated-iothread",
                class: "mb-10",
                label: _unref(t)('cluster.credential.harvester.storageOptions.dedicatedIOThread.label'),
                tooltip: _unref(t)('cluster.credential.harvester.storageOptions.dedicatedIOThread.tip'),
                mode: __props.mode,
                disabled: __props.disabled || (!isVirtio.value && !__props.value.dedicatedIOThread),
                "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => (set({ dedicatedIOThread: $event })))
              }, null, 8, ["value", "label", "tooltip", "mode", "disabled"]),
              _cache[5] || (_cache[5] = _createTextVNode()),
              _createElementVNode("div", _hoisted_7, [
                (__props.value.io === 'native')
                  ? (_openBlock(), _createBlock(_unref(Banner), {
                      key: 0,
                      color: "info",
                      label: _unref(t)('cluster.credential.harvester.storageOptions.io.nativeTip')
                    }, null, 8, ["label"]))
                  : _createCommentVNode("", true)
              ])
            ], 64))
          : _createCommentVNode("", true)
      ]))
    : _createCommentVNode("", true)
}
}

})